import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Package, FileText, GraduationCap, Dumbbell, Bus, Heart, ArrowRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';

const PROGRAM_IMAGE = "https://images.unsplash.com/photo-1548682722-0fb29c46da26?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwdm9sdW50ZWVyJTIwZ3JvdXAlMjB0ZWFtJTIwYXRobGV0aWNzJTIwbm9ucHJvZml0fGVufDB8fHx8MTc3MjU2MjcyM3ww&ixlib=rb-4.1.0&q=85";

const programs = [
  {
    icon: Package,
    title: 'Equipment Fund',
    description: 'We provide essential sports equipment to young athletes whose families cannot afford the necessary gear. From running shoes to track spikes, we ensure no child is left without what they need to compete.',
    features: ['Running shoes & athletic footwear', 'Sport-specific equipment', 'Uniforms and team gear', 'Safety equipment'],
    color: 'bg-red-600',
  },
  {
    icon: FileText,
    title: 'Registration Assistance',
    description: 'League fees, competition registrations, and membership dues can add up quickly. We cover these costs so families don\'t have to choose between necessities and their child\'s athletic dreams.',
    features: ['League registration fees', 'Competition entry fees', 'Club membership dues', 'Tournament participation'],
    color: 'bg-slate-900',
  },
  {
    icon: GraduationCap,
    title: 'Scholarship Program',
    description: 'Our scholarship program provides comprehensive support to exceptional young athletes who demonstrate both athletic promise and financial need, covering multiple aspects of their athletic journey.',
    features: ['Multi-year support available', 'Academic requirements', 'Mentorship included', 'College prep assistance'],
    color: 'bg-red-600',
  },
  {
    icon: Dumbbell,
    title: 'Training Programs',
    description: 'Access to quality coaching shouldn\'t depend on family income. We connect young athletes with professional training opportunities and skill development programs.',
    features: ['Professional coaching', 'Skills clinics', 'Summer camps', 'Year-round training'],
    color: 'bg-slate-900',
  },
  {
    icon: Bus,
    title: 'Travel Support',
    description: 'For athletes competing at regional or national levels, travel costs can be prohibitive. We help cover transportation, lodging, and meal expenses for competitions.',
    features: ['Transportation costs', 'Hotel accommodations', 'Meal stipends', 'Parent/guardian travel'],
    color: 'bg-red-600',
  },
  {
    icon: Heart,
    title: 'Family Support',
    description: 'We understand that supporting a young athlete involves the whole family. Our family support services help parents navigate the youth sports system.',
    features: ['Resource navigation', 'Parent workshops', 'Sibling support', 'Community building'],
    color: 'bg-slate-900',
  },
];

const eligibility = [
  'Child must be between ages 5-18',
  'Family demonstrates financial need',
  'Resident of an area we serve',
  'Child shows commitment to athletics',
  'Complete application submitted',
];

const Programs = () => {
  return (
    <div className="pt-20" data-testid="programs-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-red-600 font-bold uppercase tracking-widest text-sm">Our Programs</span>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mt-4">
                How We Help Young Athletes Succeed
              </h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                We offer comprehensive support programs designed to remove financial barriers at every stage of a young athlete's journey. From equipment and registration to training and travel—we've got them covered.
              </p>
              <div className="mt-8">
                <Link to="/contact" data-testid="programs-apply-btn">
                  <Button className="bg-red-600 text-white hover:bg-slate-900 rounded-none uppercase tracking-widest font-bold px-8 py-4">
                    Apply for Assistance
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={PROGRAM_IMAGE}
                  alt="Young athletes training"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="py-20 md:py-32 bg-white" data-testid="programs-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-red-600 font-bold uppercase tracking-widest text-sm">What We Offer</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-4">
              Our Support Programs
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {programs.map((program, index) => (
              <motion.div
                key={program.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="border border-slate-200 hover:shadow-xl transition-shadow"
                data-testid={`program-item-${index}`}
              >
                <div className={`${program.color} p-6`}>
                  <program.icon className="w-10 h-10 text-white" />
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-bold text-slate-900">{program.title}</h3>
                  <p className="mt-3 text-slate-600 text-sm">{program.description}</p>
                  <ul className="mt-4 space-y-2">
                    {program.features.map((feature, i) => (
                      <li key={i} className="flex items-center text-sm text-slate-700">
                        <Check className="w-4 h-4 text-red-600 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Eligibility Section */}
      <section className="py-20 md:py-32 bg-slate-900 text-white" data-testid="eligibility-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-red-500 font-bold uppercase tracking-widest text-sm">Who Qualifies</span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mt-4">
                Eligibility Requirements
              </h2>
              <p className="mt-6 text-lg text-slate-300 leading-relaxed">
                Our programs are designed to help families who face genuine financial barriers to youth athletics. We review each application individually to ensure resources go to those who need them most.
              </p>
            </div>
            <div className="bg-slate-800 p-8">
              <h3 className="font-heading text-xl font-bold mb-6">Basic Requirements</h3>
              <ul className="space-y-4">
                {eligibility.map((item, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-red-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <Link to="/contact" data-testid="eligibility-apply-btn">
                  <Button className="bg-red-600 text-white hover:bg-red-700 rounded-none uppercase tracking-widest font-bold px-8 py-4 w-full">
                    Start Application
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-red-600 font-bold uppercase tracking-widest text-sm">How to Apply</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-4">
              Application Process
            </h2>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Submit Application', desc: 'Fill out our online form with your information and needs' },
              { step: '02', title: 'Documentation', desc: 'Provide required documents to verify eligibility' },
              { step: '03', title: 'Review', desc: 'Our team reviews applications within 2-3 weeks' },
              { step: '04', title: 'Receive Support', desc: 'Approved families receive direct assistance' },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
                data-testid={`process-step-${index}`}
              >
                <div className="font-heading text-6xl font-bold text-slate-100">{item.step}</div>
                <h3 className="font-heading text-lg font-bold text-slate-900 mt-2">{item.title}</h3>
                <p className="mt-2 text-slate-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-24 bg-red-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
            Need Assistance?
          </h2>
          <p className="mt-4 text-xl text-red-100 max-w-2xl mx-auto">
            Don't let financial barriers hold your child back. Apply today and let us help them reach their potential.
          </p>
          <div className="mt-8">
            <Link to="/contact" data-testid="programs-cta-btn">
              <Button className="bg-white text-red-600 hover:bg-slate-900 hover:text-white rounded-none uppercase tracking-widest font-bold px-10 py-6 text-base">
                Contact Us to Apply
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Programs;
